
import React, { useState } from 'react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  imageUrl: string;
  slug: string;
}

interface Category {
  id: string;
  name: string;
}

const Blog = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Sample categories
  const categories: Category[] = [
    { id: 'all', name: 'All Posts' },
    { id: 'admissions', name: 'Admissions' },
    { id: 'career', name: 'Career Advice' },
    { id: 'interview', name: 'Interview Tips' },
    { id: 'resume', name: 'Resume Building' },
    { id: 'success-stories', name: 'Success Stories' }
  ];

  // Sample blog posts
  const allPosts: BlogPost[] = [
    {
      id: 'post-1',
      title: '5 Common Mistakes to Avoid in Your College Application Essays',
      excerpt: 'Learn the most common pitfalls that even strong students make when writing their application essays, and how to avoid them.',
      author: 'Emily Johnson',
      date: 'April 2, 2023',
      category: 'admissions',
      readTime: '7 min read',
      imageUrl: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=800&h=500',
      slug: 'college-application-essay-mistakes'
    },
    {
      id: 'post-2',
      title: 'How I Landed My Dream Job at Google After College',
      excerpt: 'A Harvard alum shares her journey from college to securing a position at one of the world\'s most competitive companies.',
      author: 'Michael Chang',
      date: 'March 15, 2023',
      category: 'career',
      readTime: '10 min read',
      imageUrl: 'https://images.unsplash.com/photo-1611926653458-09294b3142bf?auto=format&fit=crop&q=80&w=800&h=500',
      slug: 'landing-google-job-after-college'
    },
    {
      id: 'post-3',
      title: 'Mastering the Case Interview: Tips from a McKinsey Consultant',
      excerpt: 'An insider\'s guide to excelling in case interviews, with real examples and frameworks used by top consultants.',
      author: 'Sophia Williams',
      date: 'February 28, 2023',
      category: 'interview',
      readTime: '12 min read',
      imageUrl: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=800&h=500',
      slug: 'mastering-case-interviews'
    },
    {
      id: 'post-4',
      title: 'From Rejection to Acceptance: How I Got Into Stanford After Being Waitlisted',
      excerpt: 'A personal story of persistence, strategic communication, and making the most of a waitlist situation.',
      author: 'David Martinez',
      date: 'January 20, 2023',
      category: 'success-stories',
      readTime: '8 min read',
      imageUrl: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&q=80&w=800&h=500',
      slug: 'stanford-waitlist-success-story'
    },
    {
      id: 'post-5',
      title: 'Building a Resume That Stands Out: Advice from Top Recruiters',
      excerpt: 'Learn what recruiters from Fortune 500 companies look for in resumes and how to make yours rise to the top of the pile.',
      author: 'Jennifer Park',
      date: 'December 12, 2022',
      category: 'resume',
      readTime: '9 min read',
      imageUrl: 'https://images.unsplash.com/photo-1586281380117-5a60ae2050cc?auto=format&fit=crop&q=80&w=800&h=500',
      slug: 'standout-resume-advice'
    },
    {
      id: 'post-6',
      title: 'The Insider\'s Guide to Ivy League Admissions: Beyond the Numbers',
      excerpt: 'A former admissions officer reveals what truly matters in the holistic review process at elite universities.',
      author: 'Robert Lee',
      date: 'November 5, 2022',
      category: 'admissions',
      readTime: '11 min read',
      imageUrl: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&q=80&w=800&h=500',
      slug: 'ivy-league-admissions-guide'
    }
  ];

  // Filter posts based on search and category
  const filteredPosts = allPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === null || selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <NavBar />
      <main className="flex-grow">
        
        {/* Hero Section */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl font-bold text-gray-900 font-playfair mb-6">
                Alumni Insights Blog
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Expert advice, success stories, and insider tips from our community of alumni and students.
              </p>
              
              {/* Search Box */}
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  className="pl-10 bg-white border-gray-200"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>
        
        {/* Categories */}
        <section className="py-8 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-2 md:gap-4">
              {categories.map(category => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  className={selectedCategory === category.id 
                    ? "bg-crimson hover:bg-crimson-dark" 
                    : "border-gray-200 text-gray-700 hover:text-crimson hover:border-crimson"}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </Button>
              ))}
            </div>
          </div>
        </section>
        
        {/* Blog Posts */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {filteredPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map(post => (
                  <Card key={post.id} className="overflow-hidden border border-gray-200 hover:shadow-md transition-shadow duration-300">
                    <Link to={`/blog/${post.slug}`}>
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={post.imageUrl} 
                          alt={post.title} 
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                    </Link>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                        <span>{post.category.charAt(0).toUpperCase() + post.category.slice(1)}</span>
                        <span>{post.readTime}</span>
                      </div>
                      <Link to={`/blog/${post.slug}`}>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 hover:text-crimson">
                          {post.title}
                        </h3>
                      </Link>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">{post.date}</span>
                        <span className="text-sm font-medium text-gray-700">{post.author}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <h3 className="text-xl text-gray-600">No articles found matching your search.</h3>
                <Button 
                  className="mt-4 bg-crimson hover:bg-crimson-dark"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory(null);
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </section>
        
        {/* Newsletter Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 font-playfair mb-4">
                Stay Updated
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Subscribe to our newsletter for the latest articles, insights, and success stories.
              </p>
              <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <Input 
                  type="email" 
                  placeholder="Your email address" 
                  className="flex-grow bg-white border-gray-200" 
                  required
                />
                <Button className="bg-crimson hover:bg-crimson-dark whitespace-nowrap">
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </section>
        
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
